import type { ChatContext, ChatOpenOptions, ChatPlugin } from '#types/plugin/chat'
import { isString } from '#core/utils/is'

interface SierraConfig {
  cssPath?: string
  hideLauncher?: boolean
  launcher?: boolean
  launcherBackgroundColor?: string
}
interface SierraAPI {
  init: (config: any) => void
  cleanup: () => void
  openChatModal: () => void
  closeChatModal: () => void
  clearPersistedConversation: () => void
  endConversation: () => void
}

declare global {
  interface Window {
    sierra: SierraAPI
    sierraConfig: {
      waitForInit?: boolean
      customGreeting?: string
      onConversationStart?: () => void
      onClose?: () => void
      onOpen?: (opts: { isNewConversation: boolean }) => void
      variables?: {
        consumerId?: string | null
        accessToken?: string | null
        isLoyaltyMember?: boolean
        locale: string
        brand: string
        productId?: string
        productName?: string
      }
    }
  }
}

export default (config?: SierraConfig): ChatPlugin => {
  const { $feature, $gtm, $locale, $t } = useNuxtApp()
  const { spanishPages } = useAppConfig().pages.help
  const auth = useAuthStore()
  const user = useUserStore()
  const route = useRoute()
  const { brand, targetEnv } = useRuntimeConfig().public

  const { details, isLoyaltyMember } = storeToRefs(user)
  const { embedUrl: rawEmbedUrl } = $feature.configLivechat

  const accessToken = computed(() => auth.loggedIn ? auth.accessToken : null)

  // Handle both string and object formats for embedUrl
  const embedUrl = computed(() => {
    if (!rawEmbedUrl) return null
    return isString(rawEmbedUrl) ? rawEmbedUrl : rawEmbedUrl[targetEnv.toUpperCase()] || null
  })

  const isAvailable = ref(false)
  const sierraReady = ref(false)
  const needsSierraUpdate = ref(false)
  const translations = ref()

  const isSpanishPage = computed(() => route.path.split('/').some((page) => spanishPages?.includes(page)))
  const locale = isSpanishPage.value ? 'es-us' : $locale

  window.sierraConfig = {
    waitForInit: true,
    variables: {
      consumerId: auth.loggedIn ? details.value?.consumerId : null,
      accessToken: accessToken.value,
      brand,
      locale
    }
  }

  const loadCss = async () => {
    const cssPath = config?.cssPath
    if (cssPath && !document.querySelector(`link[href="${cssPath}"]`)) {
      return new Promise((resolve, reject) => {
        try {
          useHead({
            link: () => [
              {
                rel: 'stylesheet',
                type: 'text/css',
                href: cssPath,
                once: true,
                onload: () => resolve(true),
                // sometimes it throws errors even when loading, so we just have to wait for the event to kick in so we don't block the loading flow, thats why its resolving and not rejecting
                onerror: () => resolve(false),
              }
            ],
          })
        }
        catch (err: any) {
          reject(err)
          log.warn('failed to load livechat css', err)
        }
      })
    }
  }

  const loadSpanishContent = async () => {
    translations.value = isSpanishPage.value ? await import('#server-assets-brand/i18n/texts/es-us.json') : $t
  }

  const updateSierraWithUserData = (profileDetails) => {
    if (!profileDetails) return

    const userData = {
      variables: {
        consumerId: auth.loggedIn ? details.value?.consumerId : null,
        accessToken: accessToken.value,
        isLoyaltyMember: isLoyaltyMember.value,
        locale,
        brand
      }
    }
    Object.assign(window.sierraConfig, userData)

    const firstName = profileDetails.firstName || ''

    if (auth.loggedIn) {
      window.sierraConfig.customGreeting = isLoyaltyMember.value
        ? replaceAll(translations.value.sierraChatGreetingLoggedInAndLoyalty, {
            brandName: translations.value.brandName,
            firstName
          })
        : replaceAll(translations.value.sierraChatGreetingLoggedInAndNotLoyalty, {
            firstName
          })
    }
    else {
      window.sierraConfig.customGreeting = translations.value.sierraChatGreeting
    }
  }

  const setSierraConfig = () => {
    const sierraConfig = {
      canEndConversation: true,
      chatInterfaceStrings: {
        disclosure: translations.value.sierraChatDisclaimer
      },
      customGreeting: translations.value.sierraChatGreeting,
      dimensions: {
        width: '25rem',
        height: '40rem',
        maxHeight: 'calc(100% - 9rem)'
      },
      display: 'corner',
      titleBarLogoOnly: true
    }
    Object.assign(window.sierraConfig, sierraConfig)
  }

  const initializeLiveChatEventTracking = () => {
    window.sierraConfig.onOpen = () => $gtm?.push('livechat.onAction', 'Displayed', 'Sierra')
    window.sierraConfig.onConversationStart = () => {
      $gtm?.push('livechat.onAction', 'Started', 'Sierra')
      $gtm?.push('livechat.onAction', 'Agent Started', 'Sierra')
    }
    window.sierraConfig.onClose = () => $gtm?.push('livechat.onAction', 'Closed', 'Sierra')
  }

  const initSierra = () => {
    if (!window.sierra || !embedUrl.value) return

    window.sierra.cleanup?.()

    const config = {
      ...window.sierraConfig,
      variables: {
        ...window.sierraConfig.variables,
        consumerId: auth.loggedIn ? details.value?.consumerId : null,
        accessToken: accessToken.value,
        isLoyaltyMember: isLoyaltyMember.value,
        locale,
        brand
      }
    }

    window.sierra.init(config)
    sierraReady.value = true
  }

  const setSierraChat = async () => {
    try {
      if (!embedUrl.value) return

      await loadCss()
      await loadSpanishContent()
      setSierraConfig()
      updateSierraWithUserData(details.value)
      initializeLiveChatEventTracking()

      loadScript(embedUrl.value!, { type: 'module' }).then(() => {
        initSierra()
        isAvailable.value = true
      })
    }
    catch (err: any) {
      log.error('Failed to load Sierra chat script:', err)
      isAvailable.value = false
    }
  }

  const openChat = (input?: string | ChatOpenOptions, context?: ChatContext) => {
    context = typeof input === 'object' ? input.context : context
    if (!sierraReady.value || needsSierraUpdate.value || context) {
    // Reinitialize with latest data before opening
      updateSierraWithUserData(details.value)
      if (context) Object.assign(window.sierraConfig.variables!, context)
      initSierra()
      needsSierraUpdate.value = false
    }
    if (sierraReady.value && window.sierra) {
      window.sierra.openChatModal()
    }
    else {
      log.warn('Sierra chat not ready yet or cookie consent not given.')
    }
  }

  const closeChat = () => {
    if (window.sierra) {
      window.sierra.clearPersistedConversation()
      window.sierra.closeChatModal()
    }
  }

  watch(
    [
      () => auth.loggedIn,
      () => details.value
    ],
    ([newAuthState, newDetails], [oldAuthState, oldDetails]) => {
      const authChanged = newAuthState !== oldAuthState
      const userChanged = newDetails !== oldDetails

      if (authChanged) {
        if (!newAuthState) {
          // Logout: reinitialize immediately to clear session data and close chat
          updateSierraWithUserData(newDetails)
          initSierra()
          closeChat()
        }
        else {
          if (newDetails) {
            // Login with details already available (e.g. cached) - initialize immediately
            updateSierraWithUserData(newDetails)
            initSierra()
          }
          else {
            // Login: details not yet loaded; defer until details watcher fires
            needsSierraUpdate.value = true
          }
        }
      }
      else if (userChanged) {
        // Details updated (e.g. after login fetch completes) - reinitialize Sierra
        // immediately so its internal state reflects the updated window.sierraConfig
        // rather than waiting for the next openChat() call.
        updateSierraWithUserData(newDetails)
        initSierra()
        needsSierraUpdate.value = false
      }
    },
    { immediate: true }
  )

  setSierraChat()

  return {
    open: openChat,
    available: isAvailable,
    launcher: !config?.hideLauncher
  }
}
